import { createConsumer, type Consumer } from "@rails/actioncable";
import { api } from "@/lib/api";

let sharedConsumer: Consumer | null = null;
let sharedConsumerToken: string | null = null;
let idleChecker: (() => boolean) | null = null;
let reconnectPollId: ReturnType<typeof setInterval> | null = null;
let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null;
let reconnectGeneration = 0;
const reconnectListeners = new Set<() => void>();

/** forceCableReconnectWithCurrentToken 每次成功调度重连后递增；供 Hermes 等 re-subscribe。 */
export function getCableReconnectGeneration(): number {
  return reconnectGeneration;
}

/** 订阅 consumer 重建；返回取消函数。 */
export function subscribeCableReconnect(listener: () => void): () => void {
  reconnectListeners.add(listener);
  return () => reconnectListeners.delete(listener);
}

function notifyCableReconnect() {
  reconnectGeneration += 1;
  reconnectListeners.forEach((listener) => listener());
}

export const CABLE_RECONNECT_POLL_MS = 2_000;
export const CABLE_RECONNECT_TIMEOUT_MS = 20 * 60 * 1000;

export type CableReconnectScheduleAction = "reconnect_now" | "start_polling" | "already_polling";

/** 纯函数：续签后应立刻重连、开始轮询，或已在轮询中。 */
export function decideCableReconnectSchedule(
  isIdle: boolean,
  isPolling: boolean,
): CableReconnectScheduleAction {
  if (isIdle) return "reconnect_now";
  if (isPolling) return "already_polling";
  return "start_polling";
}

function cableUrl(accessToken: string): string {
  const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${protocol}//${window.location.host}/cable?access_token=${encodeURIComponent(accessToken)}`;
}

function isCableIdle(): boolean {
  return idleChecker ? idleChecker() : true;
}

function clearReconnectSchedule() {
  if (reconnectPollId !== null) {
    clearInterval(reconnectPollId);
    reconnectPollId = null;
  }
  if (reconnectTimeoutId !== null) {
    clearTimeout(reconnectTimeoutId);
    reconnectTimeoutId = null;
  }
}

/** Hermes 等工作区模块注册「是否可安全重连 Cable」查询；传 null 清除。 */
export function registerCableIdleChecker(fn: (() => boolean) | null) {
  idleChecker = fn;
}

/** Shared Action Cable consumer for Hermes (and future workspace channels). Recreates when JWT rotates. */
export function cableConsumer(): Consumer | null {
  const token = api.getAccessToken();
  if (!token) {
    disconnectCable();
    return null;
  }

  if (sharedConsumer && sharedConsumerToken === token) {
    return sharedConsumer;
  }

  // 若已有 consumer 且 token 不同：不要在这里强断；由 scheduleCableReconnectWhenIdle 负责
  if (sharedConsumer && sharedConsumerToken !== token) {
    return sharedConsumer;
  }

  sharedConsumerToken = token;
  sharedConsumer = createConsumer(cableUrl(token));
  return sharedConsumer;
}

export function disconnectCable() {
  clearReconnectSchedule();
  if (sharedConsumer) {
    sharedConsumer.disconnect();
    sharedConsumer = null;
    sharedConsumerToken = null;
  }
}

export function forceCableReconnectWithCurrentToken() {
  clearReconnectSchedule();
  if (sharedConsumer) {
    sharedConsumer.disconnect();
    sharedConsumer = null;
    sharedConsumerToken = null;
  }
  const consumer = cableConsumer();
  notifyCableReconnect();
  return consumer;
}

/** 续签后调度：idle 时立即重连，否则每 2s 轮询直至 idle 或约 20 分钟超时。 */
export function scheduleCableReconnectWhenIdle() {
  const action = decideCableReconnectSchedule(isCableIdle(), reconnectPollId !== null);

  if (action === "reconnect_now") {
    forceCableReconnectWithCurrentToken();
    return;
  }

  if (action === "already_polling") {
    return;
  }

  reconnectPollId = setInterval(() => {
    if (isCableIdle()) {
      forceCableReconnectWithCurrentToken();
    }
  }, CABLE_RECONNECT_POLL_MS);

  reconnectTimeoutId = setTimeout(() => {
    forceCableReconnectWithCurrentToken();
  }, CABLE_RECONNECT_TIMEOUT_MS);
}
